import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { GET_REPORT_METRICS, PROJECT_API_KEY, PROJECT_LEADS_STATUS_API } from 'src/@core/services/util/api.endpoints';

export type ReportCallType = 'all' | 'inbound' | 'outbound' | 'whatsapp';

export interface ReportFilters {
  project_id?: string | null;
  entity_id?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  call_type?: ReportCallType | string;
  scope?: 'entity' | 'project' | string;
  project_count?: number;
  granularity?: ReportGranularity | null;
  qualified_statuses?: string[];
}

export type ReportGranularity = 'day' | 'week';

export interface ReportMetrics {
  total_unique_leads: number;
  total_calls: number;
  total_connected_calls: number;
  /** Already expressed as a percentage by the backend. */
  connection_ratio: number;
  /** Seconds. */
  average_call_duration: number;
  /** Seconds. */
  total_call_duration: number;
  wa_delivered: number;
  wa_read: number;
  wa_clicked: number;
  wa_agent_replies: number;
  wa_user_replies: number;
  wa_saved: number;
  /** Legacy fields — kept optional for older API responses. */
  wa_sent?: number;
  wa_replied?: number;

  /**
   * Everything below arrives only from the extended getReportMetrics. All
   * optional so the page keeps working against an API that has not shipped it
   * yet — the new sections hide rather than render zeroes.
   */
  actual_minutes?: number;
  /**
   * Billed in 6-second pulses, so it can exceed actual_minutes. Null (not zero)
   * when the day rows in scope predate the credit counter — see data_coverage.
   */
  billed_minutes?: number | null;
  /**
   * True when billed_minutes includes a day the client's rate changed on and
   * the per-call split wasn't available. Missing on older backends.
   */
  billed_minutes_approx?: boolean;
  total_credits?: number | null;
  /** Null whenever the deployment has not declared a rupee-per-credit rate. */
  credit_rate_inr?: number | null;
  total_cost_inr?: number | null;
  cost_per_qualified_lead_inr?: number | null;
  calls_over_15_sec?: number | null;
  calls_over_15_sec_ratio?: number | null;
}

/**
 * Reports which parts of the payload are actually measured. Day rows written
 * before the detailed counters existed carry none of them, and the API omits
 * those sections rather than reporting an unmeasured zero as a finding.
 */
export interface ReportDataCoverage {
  detailed_counters: boolean;
  day_rows_in_scope: number;
  day_rows_with_counters: number;
  note?: string | null;
}

/** Distinct-lead funnel. Counted per project and summed for entity scope. */
export interface ReportFunnel {
  /** Null when the leads table could not be read. */
  leads_added?: number | null;
  leads_dialed: number;
  /** Null when the project's lead statuses have not been backfilled. */
  leads_connected?: number | null;
  lead_connectivity_rate?: number | null;
  /** Present and true when connectivity could not be derived. */
  lead_status_backfill_required?: boolean;
  /** Can exceed 100% when older leads are redialled inside the window. */
  dial_rate?: number | null;
  /** Both absent when no qualified-status definition applies. */
  qualified_leads?: number;
  qualification_rate?: number | null;
  /**
   * Present and true when the project defines goal outcomes but the stored
   * qualified counter cannot answer for them: the scope has not been
   * backfilled, or goal_outcomes was edited after the counter was written.
   * Distinct from the counts simply being absent, which means no definition
   * applies at all — and distinct from a qualified count of zero.
   */
  qualified_leads_unavailable?: boolean;
}

/** Speed-to-lead over leads whose *first* call falls inside the range. */
export interface ReportLeadTiming {
  leads_first_called: number;
  leads_with_timing: number;
  avg_minutes_to_first_call?: number | null;
  median_minutes_to_first_call?: number | null;
  not_connected_leads: number;
  avg_attempts_for_not_connected_leads?: number | null;
}

export interface ReportDistributions {
  /** Fixed key order, zero-filled: 0_15, 15_30, 30_60, 60_120, 120_180, 180_300, 300_plus. */
  duration_buckets: { [bucket: string]: number };
  /** Calls by exact outcome label, highest first. */
  call_status_counts: { [status: string]: number };
}

export interface ReportHeatmapCell {
  attempted: number;
  connected: number;
  connect_rate: number | null;
}

/** weekday ("Mon".."Sun") -> hour ("00".."23") -> cell. Sparse: quiet hours are absent. */
export interface ReportHeatmap {
  [day: string]: { [hour: string]: ReportHeatmapCell };
}

export interface ReportSeriesPoint {
  /** Date, or the Monday of the week when granularity is 'week'. */
  period_start: string;
  total_calls: number;
  total_connected_calls: number;
  connection_ratio?: number | null;
  billed_minutes?: number;
  /** Same as ReportMetrics.billed_minutes_approx, for this period only. */
  billed_minutes_approx?: boolean;
  actual_minutes?: number;
  total_credits?: number;
  total_cost_inr?: number | null;
  average_call_duration?: number;
  /**
   * Lead-level columns. With a date range these are distinct leads active in
   * the period; without one they come from the day counters and mean the leads
   * that *first* reached that stage during the period. unique_leads is the one
   * column the counters cannot supply, so it is range-only.
   */
  unique_leads?: number;
  leads_added?: number;
  leads_dialed?: number;
  leads_connected?: number;
  lead_connectivity_rate?: number | null;
  qualified_leads?: number;
  qualification_rate?: number | null;
}

export interface ReportMetricsResponse {
  filters: ReportFilters;
  metrics: ReportMetrics;
  data_coverage?: ReportDataCoverage;
  funnel?: ReportFunnel;
  lead_timing?: ReportLeadTiming;
  distributions?: ReportDistributions;
  heatmap?: ReportHeatmap;
  series?: ReportSeriesPoint[];
}

export interface LeadsStatusCount {
  /** Leads bucketed by the status they hold right now. */
  status_counts: { [status: string]: number };
  /**
   * Leads bucketed by every status they have ever held, counted once per lead
   * however many times it was stamped. Absent until the project's history has
   * been folded into the rollup, so treat missing as unknown, never as zero.
   */
  observed_status_counts?: { [status: string]: number };
  total_leads_with_status: number;
}

export interface ReportMetricsQuery {
  /** Pass exactly one of projectId (single project) or entityId ("All projects"). */
  projectId?: string;
  entityId?: string;
  /** YYYY-MM-DD. Send both or neither. */
  startDate?: string;
  endDate?: string;
  callType?: ReportCallType;
  /** Adds the per-period series behind the trend charts. Free — rollups are stored per day. */
  granularity?: ReportGranularity;
}

@Injectable({ providedIn: 'root' })
export class ReportsService {

  constructor(private http: HttpClient) { }

  getReportMetrics(query: ReportMetricsQuery): Observable<ReportMetricsResponse> {
    const headers = new HttpHeaders({
      'x-api-key': PROJECT_API_KEY,
      'Content-Type': 'application/json'
    });

    let params = new HttpParams();
    if (query.projectId) {
      params = params.set('project_id', query.projectId);
    } else if (query.entityId) {
      params = params.set('entity_id', query.entityId);
    }
    // Only a complete range is meaningful; omit for the all-time default.
    if (query.startDate && query.endDate) {
      params = params.set('start_date', query.startDate);
      params = params.set('end_date', query.endDate);
    }
    if (query.callType) {
      params = params.set('call_type', query.callType);
    }
    // Sent for all-time too: the lead-level columns of the series now come from
    // the daily counters rather than the bounded-range pass, so the trend charts
    // are complete without a date filter.
    if (query.granularity) {
      params = params.set('granularity', query.granularity);
    }

    return this.http.get<ReportMetricsResponse>(GET_REPORT_METRICS, { headers, params });
  }

  /**
   * Per-lead status rollup for one project. Single-project only — there is no
   * entity-wide form, so the Reports page can only offer the unique-lead
   * figures when a specific project is selected.
   */
  getLeadsStatusCount(projectId: string): Observable<LeadsStatusCount> {
    const headers = new HttpHeaders({
      'x-api-key': PROJECT_API_KEY,
      'Content-Type': 'application/json'
    });
    const params = new HttpParams().set('project_id', projectId);
    return this.http.get<LeadsStatusCount>(PROJECT_LEADS_STATUS_API, { headers, params });
  }
}
